import type { ITicketFeature } from '@/shared/api/types/ticket-feature-types'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/libs/cn'
import { resolveTicketFeatureName } from '@/shared/utils/ticket-feature-name'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { sameIds } from './model'

interface TourFeaturesRowProps {
  tourId: string
  tourName: string
  features: ITicketFeature[]
  selectedIds: string[]
  defaultFeatureIds: string[]
  languageCode: string
  disabled: boolean
  onToggle: (featureId: string, checked: boolean) => void
  onResetToDefault: () => void
}

export function TourFeaturesRow({
  tourId,
  tourName,
  features,
  selectedIds,
  defaultFeatureIds,
  languageCode,
  disabled,
  onToggle,
  onResetToDefault,
}: TourFeaturesRowProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const isCustomized = !sameIds(selectedIds, defaultFeatureIds)

  // Keep inactive features that are still selected so admins can see and remove them.
  const visibleFeatures = features.filter(
    (feature) => feature.isActive || selectedIds.includes(feature.id),
  )

  return (
    <section className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500" />
          )}
          <span className="truncate text-sm font-semibold text-zinc-900">{tourName}</span>
          <Badge variant={isCustomized ? 'default' : 'secondary'} className="shrink-0 text-[10px]">
            {isCustomized
              ? t('ticketConfig.tourCustomized', { defaultValue: 'Custom' })
              : t('ticketConfig.tourDefault', { defaultValue: 'Default' })}
          </Badge>
          <span className="shrink-0 text-xs text-zinc-500">
            {t('ticketConfig.selectedOfTotal', {
              selected: selectedIds.length,
              total: visibleFeatures.length,
              defaultValue: '{{selected}} of {{total}} selected',
            })}
          </span>
        </button>
        {isCustomized && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onResetToDefault}
          >
            {t('ticketConfig.tourResetToDefault', { defaultValue: 'Use config default' })}
          </Button>
        )}
      </header>

      {expanded && (
        <div className="divide-y divide-zinc-100 border-t border-zinc-100">
          {visibleFeatures.map((feature) => {
            const checked = selectedIds.includes(feature.id)
            const inputId = `${tourId}-${feature.id}`
            return (
              <label
                key={feature.id}
                htmlFor={inputId}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-zinc-50',
                  checked && 'bg-blue-50/40 hover:bg-blue-50/60',
                )}
              >
                <Checkbox
                  id={inputId}
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(value) => onToggle(feature.id, value === true)}
                />
                <Badge variant="secondary" className="w-fit font-mono text-[10px]">
                  {feature.featureKey}
                </Badge>
                <span className="text-sm font-medium text-zinc-900">
                  {resolveTicketFeatureName(feature, languageCode)}
                </span>
              </label>
            )
          })}
        </div>
      )}
    </section>
  )
}
